import { useMemo } from 'react';

/**
 * Three quality tiers based on bit depth + sample rate:
 *
 *   Hi-Res  — 24-bit, sample rate > 96 kHz  (e.g. 176.4, 192 kHz)  → gold
 *   Med-Res — 24-bit, 44.1 < sample rate ≤ 96 kHz (e.g. 48, 88.2, 96) → blue
 *   CD      — everything else with known bit depth / sample rate     → gray
 *
 * 44.1 kHz at any bit depth is CD quality, not Med-Res.
 */

const GOLD = '#C9A84C';
const BLUE = '#4A90D9';

const TIER_STYLES = {
  'hi-res': {
    tier: 'hi-res',
    label: 'Hi-Res',
    color: GOLD,
    bg: 'rgba(201,168,76,0.15)',
    border: '1px solid rgba(201,168,76,0.3)',
    solidBg: GOLD,
    solidColor: '#fff',
  },
  'med-res': {
    tier: 'med-res',
    label: 'Med-Res',
    color: BLUE,
    bg: 'rgba(74,144,217,0.15)',
    border: '1px solid rgba(74,144,217,0.3)',
    solidBg: BLUE,
    solidColor: '#fff',
  },
  cd: {
    tier: 'cd',
    label: 'CD',
    color: '#999',
    bg: 'var(--bg-secondary, rgba(150,150,150,0.12))',
    border: '1px solid var(--border, rgba(0,0,0,0.08))',
    solidBg: 'rgba(150,150,150,0.25)',
    solidColor: '#ccc',
  },
};

export function getQualityTier(bitDepth, sampleRate) {
  const bd = Number(bitDepth);
  const sr = Number(sampleRate);
  if (!bd || !sr) return null;
  if (bd >= 24 && sr > 96) return TIER_STYLES['hi-res'];
  if (bd >= 24 && sr > 44.1) return TIER_STYLES['med-res'];
  return TIER_STYLES.cd;
}

export function isHiRes(bitDepth, sampleRate) {
  const t = getQualityTier(bitDepth, sampleRate);
  return t?.tier === 'hi-res';
}

export function isMedRes(bitDepth, sampleRate) {
  const t = getQualityTier(bitDepth, sampleRate);
  return t?.tier === 'med-res';
}

export function isAboveCD(bitDepth, sampleRate) {
  const t = getQualityTier(bitDepth, sampleRate);
  return t?.tier === 'hi-res' || t?.tier === 'med-res';
}

/** Official Hi-Res Audio badge: 24+ bit depth OR sample rate above 48 kHz (handles Hz or kHz). */
export function qualifiesHiResAudioBadge(bitDepth, sampleRate) {
  const bd = Number(bitDepth);
  const srRaw = Number(sampleRate);
  if (Number.isFinite(bd) && bd >= 24) return true;
  if (!Number.isFinite(srRaw) || srRaw <= 0) return false;
  const srKhz = srRaw > 200 ? srRaw / 1000 : srRaw;
  return srKhz > 48;
}

export function showHiResAudioBadgeForTrack(track) {
  if (!track || typeof track !== 'object') return false;
  const bd =
    track.maximum_bit_depth ?? track.maxBitDepth ?? track.bitDepth ?? track.bit_depth;
  const sr =
    track.maximum_sampling_rate ?? track.maxSampleRate ?? track.sampleRate ?? track.sample_rate;
  const hasNums =
    (bd !== undefined && bd !== null && bd !== '')
    || (sr !== undefined && sr !== null && sr !== '');
  if (hasNums) return qualifiesHiResAudioBadge(bd, sr);
  return !!track.hires;
}

export function showHiResAudioBadgeForAlbum(album) {
  if (!album || typeof album !== 'object') return false;
  const bd =
    album.maxBitDepth ?? album.maximum_bit_depth ?? album.max_bit_depth ?? album.bit_depth;
  const sr =
    album.maxSampleRate ?? album.maximum_sampling_rate ?? album.sample_rate;
  const hasNums =
    (bd !== undefined && bd !== null && bd !== '')
    || (sr !== undefined && sr !== null && sr !== '');
  if (hasNums) return qualifiesHiResAudioBadge(bd, sr);
  return !!album.hires;
}

export function getQualityLabel(bitDepth, sampleRate) {
  const t = getQualityTier(bitDepth, sampleRate);
  if (t) return t.label;
  return 'Streaming';
}

export function getQualityDetail(bitDepth, sampleRate) {
  const bd = Number(bitDepth);
  const sr = Number(sampleRate);
  const parts = [];
  if (bd) parts.push(`${bd}-bit`);
  if (sr) parts.push(`${sr}kHz`);
  return parts.length > 0 ? parts.join(' / ') : null;
}

export default function useAudioQuality(bitDepth, sampleRate) {
  return useMemo(() => {
    const tier = getQualityTier(bitDepth, sampleRate);
    return {
      isHiRes: tier?.tier === 'hi-res',
      isMedRes: tier?.tier === 'med-res',
      isAboveCD: tier?.tier === 'hi-res' || tier?.tier === 'med-res',
      tier,
      label: tier?.label || 'Streaming',
      detail: getQualityDetail(bitDepth, sampleRate),
    };
  }, [bitDepth, sampleRate]);
}
